/**
 * THE SR DESIGN LIBRARY — delivered work and precedent, organised by CUSTOMER.
 *
 * This is NOT the component library. `folders.js` maps the SR Artwork Engine
 * (bezels, shanks, housings, stones, personalization) — the folders the
 * Builder's element pickers open onto and treat as PICKABLE COMPONENTS. This
 * file maps a separate root holding finished work: proofs, presentations, final
 * artwork, and the marks that did and did not land.
 *
 * THE TWO ARE DELIBERATELY SEPARATE ROOTS, and that is the whole design.
 * Putting precedent inside the engine root would put a customer's finished
 * presentation in front of a rep as a component they can drop onto a ring —
 * `folderForStep()` scopes every picker to a folder and reads everything under
 * it as a component. So `folderForStep` is untouched by this file and there is
 * NO path from a step to a design-library folder. `verify-studio.mjs` §18
 * asserts exactly that, in both directions.
 *
 * Every id here was created by us on 2026-09-08 through the Canva MCP, and read
 * back from the API, so they are all `verified: true` — unlike `backgrounds` in
 * `folders.js`, which is somebody else's folder we have not had confirmed.
 *
 * The segment keys are the Command Center's own space names (`categories.js`),
 * so the folder tree and the platform describe an account the same way.
 *
 * NAMING CONVENTION: every file is `Account · Product · Year`. Canva's folder
 * API carries no description field — there is nowhere to put a README beside a
 * folder — so the convention lives IN the folder names, where nobody can miss
 * it and nothing has to be maintained in two places.
 */

export const DESIGN_LIBRARY_ROOT = {
  id: 'FAHUorgO5ag',
  name: 'SR Design Library — Delivered Work & Precedent',
  verified: true,
}

/** The signpost folder carrying the naming convention. */
export const DESIGN_LIBRARY_README = {
  id: 'FAHUoqU-acM',
  name: '00 — READ ME · Name every file: Account · Product · Year',
  verified: true,
}

/**
 * One entry per Command Center space that sells artwork.
 * `accounts` is the per-account layer; `template` is the empty shape a new
 * account is copied from (Proofs · Presentations · Final artwork).
 */
export const DESIGN_SEGMENTS = {
  highSchool: {
    id: 'FAHUosBwObQ',
    name: '01 — High School',
    verified: true,
    accounts: { id: 'FAHUokx8-ug', name: 'Accounts — one folder per school' },
    template: {
      id: 'FAHUouRkiy0',
      proofs: 'FAHUomDPAXk',
      presentations: 'FAHUopNzPqo',
      finalArtwork: 'FAHUokoCrAI',
    },
  },
  college: {
    id: 'FAHUohG9Yhs',
    name: '02 — College',
    verified: true,
    accounts: { id: 'FAHUootufk0', name: 'Accounts — one folder per school' },
    template: {
      id: 'FAHUouS437E',
      proofs: 'FAHUomUzKe8',
      presentations: 'FAHUouOCf7g',
      finalArtwork: 'FAHUokDO-2w',
    },
  },
  // Cheer's account layer is BY GYM (see CHEER_LIBRARY) — the gym is the
  // account, the teams under it are not HubSpot records at all. It therefore
  // carries no generic `accounts`/`template` pair; inventing one would give
  // cheer two competing places to file the same proof.
  cheer: {
    id: 'FAHUoptqZZY',
    name: '03 — Cheer',
    verified: true,
    accounts: null,
    template: null,
  },
  corporate: {
    id: 'FAHUovVXBG4',
    name: '04 — Corporate',
    verified: true,
    accounts: { id: 'FAHUouYYRhU', name: 'Accounts — one folder per company' },
    template: {
      id: 'FAHUol2OntI',
      proofs: 'FAHUol3X10U',
      presentations: 'FAHUokzOZCI',
      finalArtwork: 'FAHUolLb56k',
    },
  },
  policeFire: {
    id: 'FAHUot5R4rY',
    name: '05 — Police & Fire',
    verified: true,
    accounts: { id: 'FAHUohN0EBw', name: 'Accounts — one folder per department' },
    template: {
      id: 'FAHUoo1-JXc',
      proofs: 'FAHUou_wkP0',
      presentations: 'FAHUoi382Ko',
      finalArtwork: 'FAHUok12WKk',
    },
  },
  letterwinner: {
    id: 'FAHUon3n0y4',
    name: '06 — Letterwinner',
    verified: true,
    accounts: { id: 'FAHUoh52uIQ', name: 'Accounts — one folder per institution' },
    template: {
      id: 'FAHUotI3xWc',
      proofs: 'FAHUohpnblQ',
      presentations: 'FAHUogkASBI',
      finalArtwork: 'FAHUosTLPgc',
    },
  },
  greek: {
    id: 'FAHUou01xHw',
    name: '07 — Greek',
    verified: true,
    accounts: { id: 'FAHUorQRe80', name: 'Accounts — one folder per chapter or nationals' },
    template: {
      id: 'FAHUoq6S8nk',
      proofs: 'FAHUoiyCdkk',
      presentations: 'FAHUouYRnMI',
      finalArtwork: 'FAHUorUutV8',
    },
  },
}

/**
 * CHEER — the gym is the account, the event is how presentations are found.
 *
 * `events` are REAL Varsity Spirit event names, read off varsity.com's own
 * end-of-season and school championship listings on 2026-09-08. Nothing here is
 * a guessed event: an event SR pursues that is not on this list files under
 * `otherEvent` with its exact name in the file title, because a folder named
 * for a championship that does not exist is worse than no folder.
 */
export const CHEER_LIBRARY = {
  presentationsByEvent: {
    id: 'FAHUohPTYaQ',
    name: '01 — Presentations by event · file as: Gym · Event · Year',
    verified: true,
  },
  marksApproved: {
    id: 'FAHUotpxSnw',
    name: '02 — Marks that worked (approved & landed)',
    verified: true,
  },
  // The negative half is FIRST-CLASS, not an afterthought. A rework nobody
  // recorded is a rework the next designer repeats.
  marksRejected: {
    id: 'FAHUoix41Dw',
    name: '03 — Marks reworked or rejected · put the reason in the file name',
    verified: true,
  },
  byGym: {
    id: 'FAHUorRkfno',
    name: '04 — By gym · file as: Gym · Product · Year',
    verified: true,
  },
  events: [
    { id: 'FAHUokwrKvs', name: 'The Summit' },
    { id: 'FAHUolI1Jc8', name: 'The D2 Summit' },
    { id: 'FAHUollZyG0', name: 'The Small Gym Summit' },
    { id: 'FAHUogdkgbs', name: 'The Youth Summit' },
    { id: 'FAHUok7d-NA', name: 'The Recreational Summit' },
    { id: 'FAHUot-TqKU', name: 'The Regional Summits' },
    { id: 'FAHUoj0CW5k', name: 'The Dance Summit' },
    { id: 'FAHUolm4Qfg', name: 'The Celebration' },
    { id: 'FAHUogj3CUw', name: 'The U.S. Finals' },
    { id: 'FAHUop5ZRIc', name: 'UCA National High School Cheerleading Championship (NHSCC)' },
    { id: 'FAHUogf770I', name: 'UDA National Dance Team Championship (NDTC)' },
  ],
  otherEvent: {
    id: 'FAHUok2Xkrs',
    name: 'Other event — put the exact event name in the file title',
    verified: true,
  },
  /**
   * The seeded gyms. NOT all 519 in the cheer universe — the top of the
   * competitor's own ACTIVE book, ranked by how many ring products Herff Jones
   * currently publishes for them (`src/data/hj_cheer_accounts.json`, captured
   * 2026-09-05). Product breadth is the size of the program SR would take over,
   * and ACTIVE is the only tier that supports "HJ is their vendor today".
   *
   * WHY THAT MEASURE AND NOT CHAMPIONSHIP FOOTPRINT: all 1,414 cheer
   * championships hold NO `current_champion` (CLAUDE.md), so `rollupCheer` has
   * no fuel and the footprint ranking does not exist yet. When the cheer hunt
   * fills those in, re-rank and add — this list is append-only.
   */
  gyms: [
    { id: 'FAHUokm-oZw', name: 'Cheer Athletics' },
    { id: 'FAHUoohzu9w', name: 'California Allstars' },
    { id: 'FAHUokX8bC4', name: "Mac's All-Star Cheer" },
    { id: 'FAHUoneqeJ8', name: 'Rival Athletics' },
    { id: 'FAHUomWA764', name: 'Top Gun' },
    { id: 'FAHUosz948Y', name: 'Stars Vipers Cheer & Tumbling' },
    { id: 'FAHUovzmRgw', name: 'GymTyme' },
    { id: 'FAHUosO8Pes', name: 'Stingray Allstars' },
    { id: 'FAHUokef0Bc', name: 'ATA' },
    { id: 'FAHUoilJPs0', name: 'Pittsburg Pride All Stars' },
    { id: 'FAHUoiAtcpY', name: 'All Star Revolution' },
  ],
}

/**
 * THE REP DROP — staging only.
 *
 * A library organised by person is unusable the moment somebody leaves, so this
 * is explicitly NOT the canonical organisation: it is where work lands before
 * it is filed by segment and account. Names come from the real roster
 * (`src/data/sr_team_roster.json`), never guessed.
 */
export const REP_DROP = {
  id: 'FAHUogezKL4',
  name: '99 — Rep drop (unfiled) · file it by segment + account when you can',
  verified: true,
  people: [
    { id: 'FAHUouaB15o', name: 'Philip Davanzo III' },
    { id: 'FAHUooggaKo', name: 'Dalton Demos' },
    { id: 'FAHUonIrJkM', name: 'Chris Powell' },
    { id: 'FAHUotgiUlw', name: 'Eric Graham' },
    { id: 'FAHUohTQulY', name: 'Zach Muller' },
    { id: 'FAHUogl1dVU', name: 'Jonathan Harrison' },
    { id: 'FAHUor4brsU', name: 'Faith Curtis' },
    { id: 'FAHUopANIL8', name: 'Madison Gill' },
    { id: 'FAHUoj5WcqA', name: 'Ilia Jarostchuk' },
    { id: 'FAHUomJxH10', name: 'Emily Heller' },
    { id: 'FAHUouU7q1E', name: 'Jake Highter' },
    { id: 'FAHUosqBy8s', name: 'Stephanie Pettit' },
    { id: 'FAHUoqd_tBM', name: 'Jennifer Loveday-Donovan' },
    { id: 'FAHUoj2gzc4', name: 'Abigail Mancuso' },
    { id: 'FAHUooKVzyA', name: 'Kaitlyn Crews' },
    { id: 'FAHUokZguJ4', name: 'Laura Basile' },
    { id: 'FAHUoplwstQ', name: 'Abenezer Wondweson' },
    { id: 'FAHUosBh4Go', name: 'Yohannes Solomon' },
    { id: 'FAHUoiu2rRQ', name: 'Natan Yonas' },
    { id: 'FAHUok79zWg', name: 'Tameru Mengesha' },
    { id: 'FAHUoibXTm4', name: 'Meron Negash' },
    { id: 'FAHUogYwIZ0', name: 'Nati Dejene' },
    { id: 'FAHUopRzkIY', name: 'Samuel Zeleke' },
    { id: 'FAHUoksjAyA', name: 'Abenezer Tesfaye' },
    { id: 'FAHUovW1i8g', name: 'Amanuel Tekalign' },
    { id: 'FAHUosBKFKU', name: 'Alex Abebe' },
  ],
}

/** Every design-library folder we know a name for, for breadcrumbs. */
export const DESIGN_LIBRARY_NAMES = Object.fromEntries([
  [DESIGN_LIBRARY_ROOT.id, DESIGN_LIBRARY_ROOT.name],
  [DESIGN_LIBRARY_README.id, DESIGN_LIBRARY_README.name],
  [REP_DROP.id, REP_DROP.name],
  ...Object.values(DESIGN_SEGMENTS).flatMap((s) => [
    [s.id, s.name],
    ...(s.accounts ? [[s.accounts.id, s.accounts.name]] : []),
  ]),
  [CHEER_LIBRARY.presentationsByEvent.id, CHEER_LIBRARY.presentationsByEvent.name],
  [CHEER_LIBRARY.marksApproved.id, CHEER_LIBRARY.marksApproved.name],
  [CHEER_LIBRARY.marksRejected.id, CHEER_LIBRARY.marksRejected.name],
  [CHEER_LIBRARY.byGym.id, CHEER_LIBRARY.byGym.name],
  [CHEER_LIBRARY.otherEvent.id, CHEER_LIBRARY.otherEvent.name],
  ...CHEER_LIBRARY.events.map((e) => [e.id, e.name]),
  ...CHEER_LIBRARY.gyms.map((g) => [g.id, g.name]),
  ...REP_DROP.people.map((p) => [p.id, p.name]),
])

/**
 * Which design-library folder a Command Center space opens on.
 *
 * Same rule as `folderForStep` / `folderForInput`: an unknown key opens the
 * library ROOT so a rep can always navigate by hand — never a dead picker.
 * Matching is on WORD-ish tokens, not bare substrings; `NC` inside `NCHSAA` is
 * the standing reminder of what naive `includes()` costs here.
 */
export function folderForSegment(key = '') {
  const k = String(key).toLowerCase()
  if (/cheer|gym/.test(k)) return DESIGN_SEGMENTS.cheer
  if (/high.?school|\bhs\b|k12/.test(k)) return DESIGN_SEGMENTS.highSchool
  if (/college|university|ncaa|naia|njcaa/.test(k)) return DESIGN_SEGMENTS.college
  if (/corporate|corporation|company|employee/.test(k)) return DESIGN_SEGMENTS.corporate
  if (/police|fire|hero|department|precinct/.test(k)) return DESIGN_SEGMENTS.policeFire
  if (/letterwinner|letter.?winner|alumni/.test(k)) return DESIGN_SEGMENTS.letterwinner
  if (/greek|fraternit|sororit|chapter/.test(k)) return DESIGN_SEGMENTS.greek
  return DESIGN_LIBRARY_ROOT
}
